// Sélecteur de cartes en OVERLAY DOM (refonte 16/07, demande Claude-Alain) : moteur de
// recherche (vrai <input> = clavier natif du téléphone) + grille de vignettes à défilement
// natif. Remplace l'ancien sélecteur canvas paginé (level-picker.ts) : avec le nombre de
// cartes on s'y perdait. Même famille de contrat que makeMenu (MenuHandle avec destroy())
// pour se ranger dans l'état `menu` de main.ts, PLUS update() : quand la bibliothèque ou les
// records arrivent (requêtes asynchrones), la grille se rafraîchit SANS perdre la recherche
// tapée (l'ancien pattern détruisait/recréait tout l'écran).
import { isTouch } from './platform'
import type { MenuHandle } from './screens'
import { WORLDS, type WorldKey } from '../shared/worlds'
import { levelMatches } from './search'
import { levelThumb } from './thumb'

export interface PickerEntry {
  key: string      // clé de cache de la vignette (slug, ou fam-<id>-<updatedAt>)
  title: string    // « 3. La mine »
  sub?: string     // « par Papa » (cartes famille)
  record?: string  // « ⏱ 1:23.4 Maman » : record de la CONFIG ACTUELLE (records par mode)
  rows?: string[]  // grille ASCII ; absente ou invalide = cadre neutre « ? »
  world: WorldKey
  decor?: string   // grille ASCII des décors (même largeur que rows) : rend les décos dans la vignette
}

export interface PickerUiHandle extends MenuHandle {
  update(entries: PickerEntry[], subtitle: string): void
}

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T

export function makePickerUi(
  sheet: HTMLImageElement,
  onPick: (i: number) => void,
  onBack: () => void,
): PickerUiHandle {
  const root = $('picker')
  const q = $<HTMLInputElement>('pk-q')
  const grid = $('pk-grid')
  const count = $('pk-count')
  const sub = $('pk-sub')
  const empty = $('pk-empty')
  const clearBtn = $<HTMLButtonElement>('pk-clear')

  let entries: PickerEntry[] = []

  function renderGrid(): void {
    const query = q.value
    grid.textContent = '' // détache les anciennes cartes (les canvas du cache de vignettes survivent)
    let shown = 0
    for (const [i, e] of entries.entries()) {
      // Le NOM DU BIOME est cherchable (« manoir », « neige »...) en plus du titre et de l'auteur.
      if (!levelMatches(query, [e.title, e.sub, WORLDS[e.world].name])) continue
      const card = document.createElement('button')
      card.type = 'button'
      card.className = 'pk-card'
      // Cascade d'entrée bornée à 12 crans : une longue grille ne fait pas attendre sa fin.
      card.style.setProperty('--i', String(shown % 12))
      const thumbBox = document.createElement('div')
      thumbBox.className = 'pk-thumb'
      const cv = e.rows ? levelThumb(sheet, e.key, e.rows, e.world, e.decor) : null
      if (cv) thumbBox.appendChild(cv)
      else {
        const none = document.createElement('span')
        none.className = 'pk-thumb-none'
        none.textContent = '?'
        thumbBox.appendChild(none)
      }
      const world = document.createElement('span')
      world.className = 'pk-world'
      world.textContent = WORLDS[e.world].name
      thumbBox.appendChild(world)
      const meta = document.createElement('div')
      meta.className = 'pk-meta'
      const name = document.createElement('span')
      name.className = 'pk-name'
      name.textContent = e.title
      const line2 = document.createElement('span')
      line2.className = 'pk-line2'
      if (e.sub) line2.append(e.sub)
      if (e.record) {
        if (e.sub) line2.append(' · ')
        const rec = document.createElement('span')
        rec.className = 'pk-record'
        rec.textContent = e.record
        line2.append(rec)
      }
      meta.append(name, line2)
      card.append(thumbBox, meta)
      card.addEventListener('click', () => onPick(i))
      grid.appendChild(card)
      shown++
    }
    count.textContent = query ? `${shown} / ${entries.length}` : `${entries.length} cartes`
    empty.hidden = shown > 0
    clearBtn.hidden = q.value === ''
  }

  function clearSearch(): void {
    q.value = ''
    renderGrid()
    if (!isTouch) q.focus() // au doigt, pas de focus : le clavier virtuel remonterait pour rien
  }

  function onKey(e: KeyboardEvent): void {
    if (e.code === 'Escape') {
      e.preventDefault()
      // Échap DANS une recherche = l'effacer (réflexe universel) ; recherche vide = retour menu.
      if (q.value !== '') clearSearch()
      else onBack()
    } else if (e.code === 'Enter' && e.target === q) {
      // Entrée dans le champ : lance la PREMIÈRE carte affichée (recherche -> jeu sans la souris).
      const first = grid.querySelector<HTMLButtonElement>('.pk-card')
      if (first) { e.preventDefault(); first.click() }
    }
  }

  q.addEventListener('input', renderGrid)
  clearBtn.addEventListener('click', clearSearch)
  $('pk-empty-clear').addEventListener('click', clearSearch)
  const back = (): void => onBack()
  $('pk-back').addEventListener('click', back)
  addEventListener('keydown', onKey)

  // Crochet de dev (captures headless) : ?q=manoir préremplit la recherche.
  const devQ = /[?&]q=([^&]*)/.exec(location.search)
  if (devQ) q.value = decodeURIComponent(devQ[1]!)

  root.hidden = false
  if (!isTouch) q.focus() // desktop : on arrive prêt à taper ; tactile : pas de clavier imposé

  return {
    update(newEntries: PickerEntry[], subtitle: string): void {
      entries = newEntries
      sub.textContent = subtitle
      renderGrid()
    },
    destroy(): void {
      removeEventListener('keydown', onKey)
      q.removeEventListener('input', renderGrid)
      clearBtn.removeEventListener('click', clearSearch)
      $('pk-empty-clear').removeEventListener('click', clearSearch)
      $('pk-back').removeEventListener('click', back)
      q.value = ''
      grid.textContent = ''
      root.hidden = true
    },
  }
}
